import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  adminDataApi,
  type CaseDetailDto,
  type CaseTaskDto
} from "../../services/admin-data-api";
import { useAuthStore } from "../../store/auth-store";

interface UpdateFormState {
  status: string;
  priority: string;
  assignedToId: string | null;
}

const statusOptions = ["OPEN", "IN_PROGRESS", "CLOSED"] as const;
const priorityOptions = ["LOW", "MEDIUM", "HIGH", "URGENT"] as const;

const statusColor: Record<string, "default" | "success" | "warning" | "info"> = {
  OPEN: "warning",
  IN_PROGRESS: "info",
  CLOSED: "success"
};

const priorityColor: Record<string, "default" | "info" | "warning" | "error" | "success"> = {
  LOW: "default",
  MEDIUM: "info",
  HIGH: "warning",
  URGENT: "error"
};

export const CaseDetailPage = () => {
  const { id } = useParams();
  const session = useAuthStore((state) => state.session);
  const canManage = session?.user.role === "ADMIN" || session?.user.role === "CASE_MANAGER";

  const [caseDetail, setCaseDetail] = useState<CaseDetailDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [note, setNote] = useState("" );
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [updateForm, setUpdateForm] = useState<UpdateFormState>({
    status: "OPEN",
    priority: "MEDIUM",
    assignedToId: null
  });
  const [isUpdatingCase, setIsUpdatingCase] = useState(false);

  // Task management state
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<CaseTaskDto | null>(null);
  const [taskForm, setTaskForm] = useState({
    title: "",
    dueDate: "",
    status: "OPEN"
  });
  const [isSavingTask, setIsSavingTask] = useState(false);

  const loadCase = async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await adminDataApi.getCase(id);
      setCaseDetail(data);
      setUpdateForm({
        status: data.status,
        priority: data.priority,
        assignedToId: data.assignedTo?.id ?? null
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load case");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCase().catch(() => {
      setError("Unable to load case");
      setIsLoading(false);
    });
  }, [id]);

  // Auto-dismiss success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleAddNote = async () => {
    if (!id || !note.trim()) return;
    setIsSavingNote(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await adminDataApi.addCaseNote(id, { content: note });
      setNote("");
      setSuccessMessage("Note added successfully");
      await loadCase();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to add note");
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleUpdateCase = async () => {
    if (!id) return;
    setIsUpdatingCase(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await adminDataApi.updateCase(id, {
        status: updateForm.status,
        priority: updateForm.priority,
        assignedToId: updateForm.assignedToId
      });
      setSuccessMessage("Case updated successfully");
      await loadCase();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to update case");
    } finally {
      setIsUpdatingCase(false);
    }
  };

  const handleOpenCreateTask = () => {
    setSelectedTask(null);
    setTaskForm({
      title: "",
      dueDate: dayjs().format("YYYY-MM-DD"),
      status: "OPEN"
    });
    setTaskDialogOpen(true);
  };

  const handleOpenEditTask = (task: CaseTaskDto) => {
    setSelectedTask(task);
    setTaskForm({
      title: task.title,
      dueDate: dayjs(task.dueDate).format("YYYY-MM-DD"),
      status: task.status
    });
    setTaskDialogOpen(true);
  };

  const handleCloseTaskDialog = () => {
    setTaskDialogOpen(false);
    setSelectedTask(null);
    setTaskForm({
      title: "",
      dueDate: "",
      status: "OPEN"
    });
  };

  const handleSaveTask = async () => {
    if (!id || !taskForm.title.trim()) return;
    setIsSavingTask(true);
    setError(null);
    setSuccessMessage(null);
    try {
      if (selectedTask) {
        await adminDataApi.updateTask(selectedTask.id, {
          title: taskForm.title,
          dueDate: taskForm.dueDate,
          status: taskForm.status,
          caseId: id
        });
        setSuccessMessage("Task updated successfully");
      } else {
        await adminDataApi.createTask({
          title: taskForm.title,
          dueDate: taskForm.dueDate,
          caseId: id
        });
        setSuccessMessage("Task created successfully");
      }
      await loadCase();
      handleCloseTaskDialog();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save task");
    } finally {
      setIsSavingTask(false);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    setError(null);
    setSuccessMessage(null);
    try {
      await adminDataApi.deleteTask(taskId);
      setSuccessMessage("Task deleted successfully");
      await loadCase();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to delete task");
    }
  };

  const sortedActivities = useMemo(
    () => caseDetail?.activities ?? [],
    [caseDetail]
  );

  if (isLoading) {
    return <CircularProgress sx={{ display: "block", my: 4 }} />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 4 }}>
        {error}
      </Alert>
    );
  }

  if (!caseDetail) {
    return null;
  }

  return (
    <Stack spacing={3}>
      {successMessage ? (
        <Alert
          severity="success"
          onClose={() => setSuccessMessage(null)}
          sx={{ mb: 2 }}
        >
          {successMessage}
        </Alert>
      ) : null}

      <Breadcrumbs aria-label="breadcrumb">
        <MuiLink component={Link} to="/dashboard" underline="hover" color="inherit">
          Home
        </MuiLink>
        <MuiLink component={Link} to="/cases" underline="hover" color="inherit">
          Cases
        </MuiLink>
        <Typography color="text.primary">{caseDetail.title}</Typography>
      </Breadcrumbs>

      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          {caseDetail.title}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={caseDetail.status.replace("_", " ")}
            color={statusColor[caseDetail.status] ?? "default"}
          />
          <Chip
            label={`Priority: ${caseDetail.priority}`}
            color={priorityColor[caseDetail.priority] ?? "default"}
          />
          <Typography variant="body2" color="text.secondary">
            Updated {dayjs(caseDetail.updatedAt).format("MMM D, YYYY h:mm A")}
          </Typography>
        </Stack>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Participant
        </Typography>
        <Typography>{caseDetail.participant.firstName} {caseDetail.participant.lastName}</Typography>
        <Typography variant="body2" color="text.secondary">
          {caseDetail.participant.email} {caseDetail.participant.nationalId ? `• ${caseDetail.participant.nationalId}` : ""}
        </Typography>
      </Paper>

      {canManage ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Case Settings
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Status"
                value={updateForm.status}
                onChange={(event) => setUpdateForm((prev) => ({ ...prev, status: event.target.value }))}
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status.replace("_", " ")}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Priority"
                value={updateForm.priority}
                onChange={(event) => setUpdateForm((prev) => ({ ...prev, priority: event.target.value }))}
              >
                {priorityOptions.map((priority) => (
                  <MenuItem key={priority} value={priority}>
                    {priority}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Assigned to"
                value={caseDetail.assignedTo?.email ?? "Unassigned"}
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={handleUpdateCase} disabled={isUpdatingCase}>
                {isUpdatingCase ? "Updating..." : "Update Case"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      ) : null}

      <Tabs value={activeTab} onChange={(_event, value) => setActiveTab(value)}>
        <Tab label="Tasks" />
        <Tab label="Notes" />
        <Tab label="Documents" />
        <Tab label="Timeline" />
      </Tabs>

      {activeTab === 0 ? (
        <Stack spacing={2}>
          {canManage ? (
            <Box>
              <Button variant="contained" onClick={handleOpenCreateTask}>
                Add Task
              </Button>
            </Box>
          ) : null}
          <Paper variant="outlined" sx={{ p: 2 }}>
            {caseDetail.tasks.length ? (
              <Stack spacing={1}>
                {caseDetail.tasks.map((task: CaseTaskDto) => (
                  <Box
                    key={task.id}
                    sx={{
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      pb: 1,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2">{task.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Due {dayjs(task.dueDate).format("MMM D, YYYY")} • Status: {task.status}
                      </Typography>
                    </Box>
                    {canManage ? (
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditTask(task)}
                          title="Edit task"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteTask(task.id)}
                          title="Delete task"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : null}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No tasks for this case yet.
              </Typography>
            )}
          </Paper>
        </Stack>
      ) : null}

      {activeTab === 1 ? (
        <Stack spacing={2}>
          {canManage ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Add Note
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Note"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  multiline
                  minRows={3}
                />
                <Button variant="contained" onClick={handleAddNote} disabled={isSavingNote}>
                  {isSavingNote ? "Saving..." : "Add Note"}
                </Button>
              </Stack>
            </Paper>
          ) : null}
          {caseDetail.notes.length ? (
            caseDetail.notes.map((item) => (
              <Paper key={item.id} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2">{item.author.email}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dayjs(item.createdAt).format("MMM D, YYYY h:mm A")}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {item.content}
                </Typography>
              </Paper>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No notes yet.
            </Typography>
          )}
        </Stack>
      ) : null}

      {activeTab === 2 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          {caseDetail.documents && caseDetail.documents.length ? (
            caseDetail.documents.map((doc) => (
              <Box key={doc.id} sx={{ pb: 1 }}>
                <Typography variant="subtitle2">{doc.fileName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Uploaded by {doc.uploader.email} • {dayjs(doc.createdAt).format("MMM D, YYYY h:mm A")}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No documents uploaded yet.
            </Typography>
          )}
        </Paper>
      ) : null}

      {activeTab === 3 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          {sortedActivities.length ? (
            sortedActivities.map((activity) => (
              <Box key={activity.id} sx={{ pb: 1 }}>
                <Typography variant="subtitle2">{activity.description}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {dayjs(activity.createdAt).format("MMM D, YYYY h:mm A")}
                  {activity.user ? ` • ${activity.user.email}` : ""}
                </Typography>
                {activity.metadata ? (
                  <Typography variant="caption" color="text.secondary">
                    {JSON.stringify(activity.metadata)}
                  </Typography>
                ) : null}
                <Divider sx={{ mt: 1 }} />
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No activity logged yet.
            </Typography>
          )}
        </Paper>
      ) : null}

      <Dialog open={taskDialogOpen} onClose={handleCloseTaskDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedTask ? "Edit Task" : "Create Task"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                label="Task Title"
                value={taskForm.title}
                onChange={(e) => setTaskForm((prev) => ({ ...prev, title: e.target.value }))}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Due Date"
                type="date"
                value={taskForm.dueDate}
                onChange={(e) => setTaskForm((prev) => ({ ...prev, dueDate: e.target.value }))}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {selectedTask ? (
              <Grid item xs={12}>
                <TextField
                  select
                  label="Status"
                  value={taskForm.status}
                  onChange={(e) => setTaskForm((prev) => ({ ...prev, status: e.target.value }))}
                  fullWidth
                >
                  <MenuItem value="OPEN">Open</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                </TextField>
              </Grid>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTaskDialog}>Cancel</Button>
          <Button
            onClick={handleSaveTask}
            variant="contained"
            disabled={isSavingTask || !taskForm.title || !taskForm.dueDate}
          >
            {isSavingTask ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
